import { isPrimaryPersistedTab } from 'apiConnector/tabs/tabs.model';
import type { Action } from 'state/actions';
import { addOrPatchKey, patchKey } from 'state/utils/collection';

import type { StaticWorkspaceState, StaticWorkspaceStateMap } from './static.model';
import { initialStaticWorkSpaceState } from './static.model';
export function staticWorkspacesReducer(
  state: StaticWorkspaceStateMap = initialStaticWorkSpaceState,
  action: Action,
): StaticWorkspaceStateMap {
  switch (action.type) {
    case 'WORKSPACE_PERSISTED_TABS_APPLIED':
      return action.tabs.reduce((oldState, tab) => {
        if (isPrimaryPersistedTab(tab) && tab.workspace === 'static') {
          return addOrPatchKey(oldState, tab.id, () => ({ quoteId: tab.quoteId }) as StaticWorkspaceState);
        }
        return oldState;
      }, state);
    case 'STATIC_WORKSPACE_SET_QUOTE':
      const patched: Partial<StaticWorkspaceState> = { quoteId: action.quoteId };
      return addOrPatchKey(state, action.tabId, () => patched);
    case 'STATIC_WORKSPACE_UNSET_QUOTE':
      return patchKey(state, action.tabId, () => ({ quoteId: undefined }));
    case 'WORKSPACE_TAB_PRODUCT_VARIATION_SELECTED':
      if (action.workspaceType !== 'static') {
        return state;
      }
      return addOrPatchKey(state, action.tabId, () => ({ productVariation: action.productVariation }));
    case 'WORKSPACE_ADVISORY_TAB_CREATED':
      if (action.workspace !== 'static') {
        return state;
      }
      return addOrPatchKey(state, action.id, () => ({ isProductValidated: true, isFetchingTabContent: false }));
    case 'WORKSPACE_TAB_CLIENT_SELECTED':
      if (action.workspaceType !== 'static') {
        return state;
      }
      return addOrPatchKey(state, action.tabId, () => ({ client: action.client }));
    case 'WORKSPACE_TAB_CONTENT_FETCHED':
      if (action.workspace !== 'static') {
        return state;
      }
      return addOrPatchKey(state, action.tabId, () => ({ isProductValidated: true, isFetchingTabContent: false }));
    case 'DEFAULT_FORM_FAILED':
    case 'DEFAULT_FORM_RECEIVED':
      if (action.workspace !== 'static') {
        return state;
      }
      return addOrPatchKey(state, action.tabId, () => ({ isProductValidated: true, isFetchingTabContent: false }));
    case 'WORKSPACE_TAB_CONTENT_FETCH_REQUESTED':
      if (action.workspace !== 'static') {
        return state;
      }
      return addOrPatchKey(state, action.tabId, () => ({ isFetchingTabContent: true }));
    case 'WORKSPACE_TAB_CONTENT_FETCH_FAILED':
      if (action.workspace !== 'static') {
        return state;
      }
      return addOrPatchKey(state, action.tabId, () => ({
        isProductValidated: action.tabType === 'reprice',
        isFetchingTabContent: false,
      }));
    case 'WORKSPACE_TAB_CLONED':
      if (action.workspace !== 'static') {
        return state;
      }
      return addOrPatchKey(state, action.newTabId, () => ({ isProductValidated: true, isFetchingTabContent: true }));
    case 'WORKSPACE_TAB_QUOTE_REPRICED':
      if (action.workspace !== 'static') {
        return state;
      }
      return addOrPatchKey(state, action.tabId, () => ({
        isProductValidated: true,
        isFetchingTabContent: true,
        aiGeneratedFields: action.aiGeneratedFields,
      }));
    case 'FORM_ENRICHMENT_REQUESTED':
      if (action.workspace !== 'static') {
        return state;
      }
      const userEditedFields = Object.keys(action.patch);
      return addOrPatchKey(state, action.tabId, ({ aiGeneratedFields: previousFields }) => ({
        aiGeneratedFields: previousFields?.filter(f => !userEditedFields.includes(f)),
      }));
    case 'WORKSPACE_RFQ_LOAD_REQUESTED':
      if (action.workspace !== 'static') {
        return state;
      }
      return addOrPatchKey(state, action.tabId, () => ({ isProductValidated: true, isFetchingTabContent: true }));
    case 'WORKSPACE_TAB_PRODUCT_SELECTION_SUBMITTED':
      if (action.workspaceType !== 'static') {
        return state;
      }
      return addOrPatchKey(state, action.tabId, () => ({
        isProductValidated: true,
        isFetchingTabContent: true,
        productVariation: action.productVariation,
        client: action.client,
      }));
    default:
      return state;
  }
}
